/**
 * Accessibility fix (WCAG 2.2.2 — Pause, Stop, Hide)
 *
 * Several PDP/marketing images (e.g. technology-feature graphics) are
 * animated .gif files embedded as plain <img> tags. A native animated GIF
 * cannot be paused via any HTML attribute once embedded this way, so this
 * script finds every such image sitewide and adds an accessible toggle that
 * freezes it on a canvas snapshot of the current frame and lets the user
 * resume it. If prefers-reduced-motion is set, GIFs are frozen automatically
 * on load rather than waiting for interaction.
 *
 * This is asset-format-driven (matches on file extension) rather than tied to
 * any specific template, since these images can appear in many different
 * generic image/media blocks across the theme.
 */
(function () {
  'use strict';

  var STYLE_ID = 'gif-pause-toggle-styles';
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var processed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var processedFallback = [];

  function alreadyProcessed(el) {
    if (processed) return processed.has(el);
    return processedFallback.indexOf(el) !== -1;
  }

  function markProcessed(el) {
    if (processed) { processed.add(el); return; }
    processedFallback.push(el);
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.gif-pause-wrapper{position:relative;display:inline-block;line-height:0;' +
      /* Reset: this span can land inside a .btn--text button (e.g. the
         merged USP tile trigger), whose sitewide ".btn--text span" rule
         adds padding, an underline-reveal background, and a line-height
         meant for text links -- none of which belong on an image wrapper.
         Explicitly override them here so this component's layout doesn't
         silently break depending on what happens to wrap it. */
      'padding:0!important;line-height:0!important;background:none!important;' +
      'width:100%;height:100%;}' +
      '.gif-pause-wrapper canvas{position:absolute;top:0;left:0;width:100%;height:100%;display:none;}' +
      '.gif-pause-toggle{position:absolute;z-index:5;bottom:8px;right:8px;' +
      'width:28px;height:28px;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(255,255,255,0.9);border:1px solid rgba(0,0,0,0.15);border-radius:50%;' +
      'cursor:pointer;padding:0;opacity:0;transition:opacity 0.15s ease;}' +
      '.gif-pause-wrapper:hover .gif-pause-toggle,' +
      '.gif-pause-toggle:focus,' +
      '.gif-pause-toggle:focus-visible{opacity:1;}' +
      '.gif-pause-toggle:focus-visible{outline:2px solid #000;outline-offset:2px;}' +
      '.gif-pause-toggle__icon{width:11px;height:11px;display:block;}';
    document.head.appendChild(style);
  }

  function isAnimatedGif(img) {
    var src = img.currentSrc || img.getAttribute('src') || '';
    return /\.gif(\?|$)/i.test(src);
  }

  function wrapImage(img) {
    if (alreadyProcessed(img)) return;
    markProcessed(img);
    injectStyles();

    // Accessibility fix: if this GIF is already inside a link or button
    // (e.g. a small icon inside a clickable tile), don't add our own
    // nested <button> — a button inside a link/button is invalid, nested-
    // interactive markup, and its click wasn't isolated from the parent's
    // click either, so clicking "pause" was also firing whatever the
    // parent link does. In that case we still respect prefers-reduced-
    // motion by freezing on load, we just don't add a separate control.
    var insideClickableAncestor = !!img.closest('a, button, [role="button"]');

    var wrapper = document.createElement('span');
    wrapper.className = 'gif-pause-wrapper';
    img.parentNode.insertBefore(wrapper, img);
    wrapper.appendChild(img);

    var canvas = document.createElement('canvas');
    wrapper.appendChild(canvas);

    var btn = null;
    if (!insideClickableAncestor) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gif-pause-toggle';
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'Pause animation');
      btn.innerHTML =
        '<svg class="gif-pause-toggle__icon gif-pause-toggle__icon--pause" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" fill="currentColor"/><rect x="14" y="5" width="4" height="14" fill="currentColor"/></svg>' +
        '<svg class="gif-pause-toggle__icon gif-pause-toggle__icon--play" viewBox="0 0 24 24" aria-hidden="true" focusable="false" hidden><path d="M7 4l14 8-14 8V4z" fill="currentColor"/></svg>';
      wrapper.appendChild(btn);
    }

    var pauseIcon = btn ? btn.querySelector('.gif-pause-toggle__icon--pause') : null;
    var playIcon = btn ? btn.querySelector('.gif-pause-toggle__icon--play') : null;
    var isFrozen = false;
    var originalSrc = img.currentSrc || img.src;

    function freeze() {
      if (!img.naturalWidth) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      var ctx = canvas.getContext('2d');
      try {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      } catch (e) {
        return;
      }
      canvas.style.display = 'block';
      img.style.visibility = 'hidden';
      isFrozen = true;
      if (btn) {
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Play animation');
      }
      if (pauseIcon) pauseIcon.hidden = true;
      if (playIcon) playIcon.hidden = false;
    }

    function resume() {
      canvas.style.display = 'none';
      img.style.visibility = 'visible';
      // Force the GIF to restart/continue animating by re-requesting the src.
      img.src = originalSrc;
      isFrozen = false;
      if (btn) {
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Pause animation');
      }
      if (pauseIcon) pauseIcon.hidden = false;
      if (playIcon) playIcon.hidden = true;
    }

    if (btn) {
      btn.addEventListener('click', function (e) {
        // Isolate this control's click from any clickable ancestor,
        // belt-and-suspenders in case a future change re-nests this
        // inside a link/button again.
        e.stopPropagation();
        if (isFrozen) {
          resume();
        } else {
          freeze();
        }
      });
    }

    if (prefersReducedMotion) {
      if (img.complete) {
        freeze();
      } else {
        img.addEventListener('load', freeze, { once: true });
      }
    }
  }

  function scan(root) {
    root = root || document;
    var images = root.querySelectorAll ? root.querySelectorAll('img') : [];
    images.forEach(function (img) {
      if (isAnimatedGif(img)) {
        wrapImage(img);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    scan(document);
  });
  document.addEventListener('shopify:section:load', function (evt) {
    scan(evt.target);
  });

  /**
   * Nosto (and potentially other apps) replaces/injects the entire product
   * grid on the PLP client-side, on its own timeline, with no theme event
   * to hook into. shopify:section:load never fires for that. A
   * MutationObserver catches any new <img> added anywhere on the page,
   * regardless of which script put it there, so GIFs inside a
   * Nosto-rendered grid still get the pause/play toggle.
   */
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // element nodes only
          if (node.tagName === 'IMG') {
            if (isAnimatedGif(node)) wrapImage(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        });
      });
    });
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();
